import React from 'react';
import { Box, Flex, HStack, VStack, Text, IconButton, Tooltip, Tag } from '@chakra-ui/react';
import { Handle, Position } from 'reactflow';
import { FiDatabase, FiEdit, FiCopy, FiTrash2, FiZap, FiRefreshCw, FiCheckCircle, FiLock, FiSend } from 'react-icons/fi';

const ApiNode = ({ data }) => {
  const executionMode = data.executionMode || 'sync';
  const method = data.apiMethod || data.method || '';
  const endpoint = data.apiEndpoint || data.endpoint || '';
  const hasRetry = data.retryEnabled || false;
  const authType = data.authType || 'none';
  const hasResponseMapping = data.responseMapping && data.responseMapping.length > 0 && data.responseMapping.some(m => m.responseField);
  const hasQueryParams = data.queryParams && data.queryParams.length > 0 && data.queryParams.some(p => p.key);
  const hasHeaders = data.headers && data.headers.length > 0 && data.headers.some(h => h.key);
  const hasContractFields = data.contractFields && data.contractFields.length > 0;

  const methodColors = {
    GET: 'green',
    POST: 'blue',
    PUT: 'orange',
    PATCH: 'yellow',
    DELETE: 'red',
  };
  const methodColor = methodColors[method] || 'gray';

  const modeConfig = {
    async: { icon: <FiSend size={10} />, label: 'Async', color: 'orange' },
    sync: { icon: <FiZap size={10} />, label: 'Sync', color: 'purple' },
  };
  const currentMode = modeConfig[executionMode] || modeConfig.sync;

  const authLabels = {
    none: null,
    apikey: 'API Key',
    bearer: 'Bearer',
    basic: 'Basic',
  };

  const truncateEndpoint = (url, maxLen = 28) => {
    if (!url) return 'No endpoint set';
    if (url.length <= maxLen) return url;
    try {
      const u = new URL(url);
      const path = u.pathname + u.search;
      const host = u.hostname.length > 15 ? u.hostname.substring(0, 12) + '...' : u.hostname;
      return host + (path.length > 15 ? path.substring(0, 12) + '...' : path);
    } catch {
      return url.substring(0, maxLen) + '...';
    }
  };

  return (
    <Box
      bg="white"
      borderWidth="2px"
        borderColor={data.selected ? '#ad46ff' : 'gray.300'}

      borderRadius="xl"
      boxShadow="lg"
      minW="240px"
      maxW="280px"
      transition="all 0.2s"
      _hover={{ boxShadow: 'xl', borderColor: 'purple.500' }}
      position="relative"
    >
      <Handle type="target" position={Position.Left} style={{ background: '#805AD5', width: 10, height: 10 }} />

      {/* Header */}
      <Flex
               bgGradient="linear(to-r, #9810fa, #ad46ff)" 

        color="white"
        px={3}
        py={2}
        borderTopRadius="xl"
        align="center"
        justify="space-between"
      >
        <HStack spacing={2}>
          <FiDatabase size={14} />
          <Text noOfLines={1} fontSize="xs" fontWeight="semibold">API Call</Text>
        </HStack>
        <HStack spacing={0}>
          <IconButton
            size="xs"
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.300' }}
            icon={<FiEdit size={12} />}
            aria-label="Edit"
            onClick={(e) => { e.stopPropagation(); data.onEdit?.(data.id); }}
          />
          <IconButton
            size="xs"
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.300' }}
            icon={<FiCopy size={12} />}
            aria-label="Duplicate"
            onClick={(e) => { e.stopPropagation(); data.onDuplicate?.(data.id); }}
          />
          <IconButton
            size="xs"
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.300' }}
            icon={<FiTrash2 size={12} />}
            aria-label="Delete"
            onClick={(e) => { e.stopPropagation(); data.onDelete?.(data.id); }}
          />
        </HStack>
      </Flex>

      {/* Content */}
      <Box p={3}>
        <Text fontSize="sm" fontWeight="semibold" mb={2} noOfLines={1}>
          {data.label || 'API Call'}
        </Text>

        <VStack spacing={2} align="stretch">
          {method ? (
            <HStack spacing={2} align="center">
              <Tag
                colorScheme={methodColor}
                fontSize="9px"
                size={"sm"}
                fontWeight="bold"
              
                flexShrink={0}
              >
                {method}
              </Tag>
              <Tooltip label={endpoint || 'No endpoint set'} fontSize="xs" placement="top">
                <Text fontSize="10px" color="gray.600" noOfLines={1} fontFamily="mono" flex={1}>
                  {truncateEndpoint(endpoint)}
                </Text>
              </Tooltip>
            </HStack>
          ) : (
            <Text fontSize="xs" color="gray.400" fontStyle="italic">Not configured</Text>
          )}

          {/* Badges Row 1: Mode + Auth */}
          <HStack spacing={1} flexWrap="wrap">
            <Tag
              colorScheme={currentMode.color}
              fontSize="9px"
              
              
              display="flex"
              alignItems="center"
              gap="2px"
            >
              {currentMode.icon} {currentMode.label}
            </Tag>

            {authLabels[authType] && (
              <Tag
                fontSize="9px"
                
                colorScheme="cyan"
                display="flex"
                alignItems="center"
                gap="2px"
              >
                <FiLock size={8} /> {authLabels[authType]}
              </Tag>
            )}
          </HStack>

          {/* Badges Row 2: Features */}
          <HStack spacing={1} flexWrap="wrap">
            {hasRetry && (
              <Tag
                fontSize="9px"
                
                colorScheme="yellow"
                variant="subtle"
                display="flex"
                alignItems="center"
                gap="2px"
              >
                <FiRefreshCw size={8} /> Retry {data.retryAttempts || 3}x
              </Tag>
            )}

            {hasResponseMapping && (
              <Tag
                fontSize="9px"
                
                colorScheme="green"
                variant="subtle"
                display="flex"
                alignItems="center"
                gap="2px"
              >
                <FiCheckCircle size={8} /> Mapped
              </Tag>
            )}

            {hasQueryParams && (
              <Tag fontSize="9px"  colorScheme="teal" variant="subtle">
                ?params
              </Tag>
            )}

            {hasHeaders && (
              <Tag   fontSize="9px"  colorScheme="gray" variant="subtle">
                Headers
              </Tag>
            )}

            {hasContractFields && (
              <Tag fontSize="9px"  colorScheme="blue" variant="subtle">
                Contract
              </Tag>
            )}
          </HStack>

          {executionMode === 'sync' && data.timeout && data.timeout !== 30 && (
            <Text fontSize="9px" color="gray.400">
              Timeout: {data.timeout}s
            </Text>
          )}
        </VStack>
      </Box>

      {/* ===== OUTPUT HANDLES ===== */}
      {executionMode === 'sync' ? (
        <>
          {/* Success Handle (2xx) */}
          {/* <Box position="absolute" right="-58px" top="40%" transform="translateY(-50%)">
            <Text fontSize="8px" color="green.600" fontWeight="bold">2xx ✓</Text>
          </Box> */}
          <Handle
            type="source"
            position={Position.Right}
            id="success"
            style={{
              background: '#38A169',
              width: 10,
              height: 10,
              top: '40%',
            }}
          />

          {/* Error Handle (4xx/5xx) */}
          {/* <Box position="absolute" right="-58px" top="60%" transform="translateY(-50%)">
            <Text fontSize="8px" color="red.500" fontWeight="bold">Error ✗</Text>
          </Box> */}
          <Handle
            type="source"
            position={Position.Right}
            id="error"
            style={{
              background: '#E53E3E',
              width: 10,
              height: 10,
              top: '60%',
            }}
          />
        </>
      ) : (
        /* Async: single output handle */
        <Handle
          type="source"
          position={Position.Right}
          style={{ background: '#805AD5', width: 10, height: 10 }}
        />
      )}
    </Box>
  );
};

export default ApiNode;